import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAuthStore } from "./auth/store";
import { AppLayout } from "./layout/AppLayout";
import { AssistantPage } from "./pages/AssistantPage";
import { EntityFormPage } from "./pages/EntityFormPage";
import { EntityListPage } from "./pages/EntityListPage";
import { HelpPage } from "./pages/HelpPage";
import { HomePage } from "./pages/HomePage";
import { HubPage } from "./pages/HubPage";
import { ProfilePage } from "./pages/ProfilePage";
import { LoginPage } from "./pages/LoginPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CompanyPage } from "./pages/CompanyPage";
import { DispatcherPage } from "./pages/DispatcherPage";
import { DockSchedulePage } from "./pages/DockSchedulePage";
import { ReportPage } from "./pages/ReportPage";
import { PackingListPage } from "./pages/PackingListPage";
import { ReportsPage } from "./pages/ReportsPage";
import { StockSummaryPage } from "./pages/StockSummaryPage";
import { TopologyPage } from "./pages/TopologyPage";
import { WorkPage } from "./pages/WorkPage";
import { AttentionPage } from "./workspace/AttentionPage";
import { DocumentJournalPage } from "./pages/DocumentJournalPage";
import { BillingOperationsPage } from "./pages/BillingOperationsPage";
import { ActSigningPage } from "./pages/ActSigningPage";
import { DocumentTemplatesPage } from "./pages/DocumentTemplatesPage";
import { KzDataCheckPage } from "./pages/KzDataCheckPage";
import { DocumentTemplatePage } from "./pages/DocumentTemplatePage";
import { PortalLayout, RequirePortalAuth } from "./portal/PortalLayout";
import { PortalLoginPage } from "./portal/PortalLoginPage";
import { PortalHomePage } from "./portal/PortalHomePage";
import { PortalRequestsPage } from "./portal/PortalRequestsPage";
import { PortalOperationsPage } from "./portal/PortalOperationsPage";
import { PortalStockPage } from "./portal/PortalStockPage";
import { PortalFinancePage } from "./portal/PortalFinancePage";
import { PortalPasswordPage } from "./portal/PortalPasswordPage";
import { PortalSettingsPage } from "./portal/PortalSettingsPage";
import { SignLinkPage } from "./portal/SignLinkPage";

/**
 * One component instance per record. Without the key React reuses the previous card's form when the URL changes
 * (partner → «new contact»), and its values leak into the new record: the contact's full name was prefilled with
 * the company name (deep testing report, section 7). Lists get the same treatment so search and paging don't
 * travel from one list to another.
 */
function EntityFormRoute() {
  const { entity = "", id = "" } = useParams();
  const { search } = useLocation();
  return <EntityFormPage key={`${entity}/${id}${id === "new" ? search : ""}`} />;
}

function EntityListRoute() {
  const { entity = "" } = useParams();
  return <EntityListPage key={entity} />;
}

function RequireAuth() {
  const token = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* ТЗ 1.2 раздел 14 / NAV-05: the client cabinet has its own entrance, session and menu */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal" element={<RequirePortalAuth />}>
        <Route element={<PortalLayout />}>
          <Route index element={<PortalHomePage />} />
          <Route path="requests" element={<PortalRequestsPage />} />
          <Route path="operations" element={<PortalOperationsPage />} />
          <Route path="stock" element={<PortalStockPage />} />
          <Route path="finance" element={<PortalFinancePage />} />
          <Route path="password" element={<PortalPasswordPage />} />
          {/* ТЗ «Рабочие столы» 31.10.2026, 6.1: «Настройки → Оформление» of the cabinet (with use_personal_workspace) */}
          <Route path="settings" element={<PortalSettingsPage />} />
        </Route>
      </Route>
      {/* ТЗ 1.2 раздел 13.3 / C05: the limited signing link needs no cabinet session */}
      <Route path="/sign/:token" element={<SignLinkPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          {/* the bare domain shows the landing page (nginx); inside the app the dashboard lives at /dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<HomePage />} />
          <Route path="assistant" element={<AssistantPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="catalog" element={<HubPage hub="catalog" />} />
          <Route path="admin" element={<HubPage hub="admin" />} />
          <Route path="company" element={<CompanyPage />} />
          <Route path="work" element={<WorkPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="topology" element={<TopologyPage />} />
          <Route path="stock-summary" element={<StockSummaryPage />} />
          <Route path="dispatcher" element={<DispatcherPage />} />
          <Route path="dock-schedule" element={<DockSchedulePage />} />
          <Route path="documents" element={<DocumentJournalPage />} />
          {/* ТЗ «Рабочие столы» 31.10.2026 (UXW-28): «Показать все N» of «Требует внимания» — the bases of one group */}
          <Route path="attention" element={<AttentionPage />} />
          {/* UX3010-09: completed operations of a client → the register of the chosen ones */}
          <Route path="billing/operations" element={<BillingOperationsPage />} />
          {/* CW31-B16: «Подписать от исполнителя» of the current version of an act */}
          <Route path="billing/acts/:id/sign" element={<ActSigningPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/:code" element={<ReportPage />} />
          <Route path="packing-lists/:number" element={<PackingListPage />} />
          {/* KZ-DOC TMP01 (30.10.2026): «Администрирование → Шаблоны документов» and the card of a template */}
          <Route path="document-templates" element={<DocumentTemplatesPage />} />
          <Route path="document-templates/:id" element={<DocumentTemplatePage />} />
          {/* KZ 30.10.2026 (ТЗ реквизитов §15.1): «Администрирование → Проверка данных под Казахстан» (read-only) */}
          <Route path="admin/kz-data-check" element={<KzDataCheckPage />} />
          <Route path=":entity" element={<EntityListRoute />} />
          <Route path=":entity/:id" element={<EntityFormRoute />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
